import { Stack, PinInput, useToast, useDisclosure } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { PATHS } from '../../router/routes';
import { zodResolver } from '@hookform/resolvers/zod';
import CodeInput from '../../components/custom-components/Inputs/CodeInput';
import ZaraButton from '../../components/custom-components/buttons/Button';
import z from 'zod';
import { useNavigate } from 'react-router-dom';
import { VerifyCode } from '../../graphql/mutations/VerifyCode';
import { useMutation } from '@apollo/client';
import { ResendVerifCode } from '../../graphql/mutations/ResendVerifCode';
import { useAuth } from '../../provider';
import { useError } from '../../error-provider/ErrorProvider';
import { useCreateScript } from '../../hooks';
import RefreshPhone from '../../components/modals/RefreshPhone';
import { useUserData } from '../../context/UserDataProvider';

const CODE_MAX_VALUE = 6;

const schema = z.object({
    code: z.string().length(CODE_MAX_VALUE),
});

const LoadingModal = () => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600">
        <div className="bg-white p-8 rounded-xl shadow-2xl flex flex-col items-center">
            <div className="loader mb-4" style={{ width: 40, height: 40, border: '4px solid #6366f1', borderTop: '4px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            <p className="text-lg text-gray-700">Loading user info...</p>
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
);

const Verification = () => {
    const { control, watch, handleSubmit } = useForm({
        mode: 'onSubmit',
        resolver: zodResolver(schema),
    });

    const { onOpen, onClose, isOpen } = useDisclosure();

    const createScript = useCreateScript();

    const { userData, isLoading: userLoading, fetchUserData } = useUserData();

    // Safely extract user_info and email
    const user_info = (userData && userData.user_info) ? userData.user_info : {};
    const email = user_info.email || localStorage.getItem('email') || '';
    if (localStorage.getItem('email') === null && user_info.email) {
        localStorage.setItem('email', user_info.email);
    }

    // Show loading modal if user_info is empty


    const { showError } = useError();
    const { verification } = useAuth();
    const router = useNavigate();
    const toast = useToast();

    const [onVerifyCode] = useMutation(VerifyCode, {
        onError(error) {
            showError(undefined, error);
        },
    });
    const [onResendCode] = useMutation(ResendVerifCode, {
        onError(error) {
            showError(undefined, error);
        },
    });
    const [loading, setLoading] = useState(false);
    const [isCodeSent, setIsCodeSent] = useState(false);

    const code = watch('code');

    const isDisabled = useMemo(() => String(code).length !== CODE_MAX_VALUE, [code]);
    const isForgotPasswordPage = useMemo(() => localStorage.getItem('fromWhere') === PATHS.FORGOT_PASSWORD, []);


    const maskedNumber = () => {
        if (user_info?.phone_number) {
            const originalNumber = user_info.phone_number;
            const prefix = originalNumber.slice(0, 4);
            const suffix = originalNumber.slice(-2);

            return prefix + '*'.repeat(originalNumber.length - 4) + suffix;
        } else {
            return undefined;
        }
    };

    function onSubmit(data) {
        setLoading(true);
        const variables = {
            email: email,
            code: data.code,
        };

        onVerifyCode({
            variables,
            onCompleted(data) {
                if (data.verifyCode.__typename === 'ErrorType') {
                    setLoading(false);
                    toast({
                        status: 'error',
                        title: data.verifyCode.message,
                        isClosable: true,
                    });
                }

                if (data.verifyCode.__typename === 'VerifyCodeSuccessType') {
                    toast({
                        status: 'success',
                        title: data.verifyCode.message,
                        isClosable: true,
                    });

                    localStorage.setItem('remindMe', true);

                    if (isForgotPasswordPage) {
                        setIsCodeSent(true);
                        localStorage.setItem('code', variables.code);
                        router(PATHS.CHANGE_PASSWORD);
                    } else {
                        if (window.fbq) {
                            window.fbq('track', 'Lead');
                        }
                        verification(data?.verifyCode?.token);
                        window.location.href = PATHS.COLLECT_COMPANY_DETAILS;
                    }
                }
            },
        });
    }

    function onResendCodeHandler() {
        if (!isForgotPasswordPage) {
            onOpen();
            return;
        }

        onResendCode({
            variables: {
                email: email,
                type: 2,
            },
            onCompleted(data) {
                if (data.resendVerificationCode.__typename === 'ErrorType') {
                    toast({
                        status: 'error',
                        title: data.resendVerificationCode.message,
                        isClosable: true,
                    });
                }

                if (data.resendVerificationCode.__typename === 'SuccessType') {
                    toast({
                        status: 'success',
                        title: data.resendVerificationCode.message,
                        isClosable: true,
                    });
                }
            },
        });
    }

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://cdn.trackdesk.com/tracking.js';
        script.async = true;

        const email = user_info.email ?? '';
        const phone = user_info.phone_number ?? '';

        const scriptContent = `
            twq('event', 'tw-on70k-on70m', {
                email_address: '${email}',
                phone_number: '${phone}'
            });
        `;

        script.onload = () => {
            (function (t, d, k) {
                (t[k] = t[k] || []).push(d);
                t[d] =
                    t[d] ||
                    t[k].f ||
                    function () {
                        (t[d].q = t[d].q || []).push(arguments);
                    };
            })(window, 'trackdesk', 'TrackdeskObject');

            if (window.trackdesk) {
                window.trackdesk('dialzara', 'externalCid', {
                    externalCid: `${user_info.email ?? ''}`,
                    revenueOriginId: 'dd8afceb-6d2b-4653-96b8-44ea771c4c29',
                });
            }
        };

        const fbqScript = createScript(`fbq('track', 'Lead');`);
        const cometScript = createScript(`comet('lead_generated');`);
        const gtagScript = createScript(
            `gtag('event', 'conversion', {'send_to': 'AW-11267532153/TdzqCMf6tYwZEPnK4_wp'});`
        );
        const twitterLeads = createScript(scriptContent);

        document.body.appendChild(twitterLeads);

        document.body.appendChild(fbqScript);
        document.body.appendChild(cometScript);
        document.body.appendChild(gtagScript);
        document.head.appendChild(script);

        return () => {
            document.head.removeChild(script);
            document.body.removeChild(twitterLeads);
            document.body.removeChild(fbqScript);
            document.body.removeChild(cometScript);
            document.body.removeChild(gtagScript);
        };
    }, [userData, createScript, user_info.email, user_info.phone_number]);

    // If user info is loading, show nothing (or loader if you prefer)
    if (userLoading) return null;
    if (!user_info || Object.keys(user_info).length === 0) {
        return <LoadingModal />;
    }

    // If no phone number, show RefreshPhone modal only
    if (!user_info.phone_number) {
        return <RefreshPhone isOpen={true} onClose={() => {
            fetchUserData();
            onClose();
        }} isNew={true} />;
    }

    // Otherwise show verification UI
    return (
        <>
            <div className='min-h-screen bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center p-4'>
                <div className='w-full max-w-md space-y-6 bg-white p-6 rounded-xl shadow-2xl relative'>
                    <div className='text-center'>
                        <h2 className='mt-2 text-2xl sm:text-3xl font-bold text-gray-900'>
                            {`Check your ${isForgotPasswordPage ? 'mail' : 'phone'}`}
                        </h2>
                        <p className='mt-2 text-sm text-gray-600'>
                            {isForgotPasswordPage
                                ? `We've sent an email to ${email}`
                                : `We've sent a SMS to ${(maskedNumber())}`}
                        </p>
                    </div>
                    <form onSubmit={handleSubmit(onSubmit)} className='mt-6 space-y-4'>
                        <div className='flex justify-center'>
                            <Controller
                                control={control}
                                name='code'
                                render={({ field: { onChange }, fieldState: { error } }) => (
                                    <Stack direction='row' justify='center' spacing='5'>
                                        <PinInput
                                            placeholder=''
                                            type='number'
                                            onChange={onChange}
                                            isInvalid={!!error?.message}
                                        >
                                            <CodeInput />
                                            <CodeInput />
                                            <CodeInput />
                                            <CodeInput />
                                            <CodeInput />
                                            <CodeInput />
                                        </PinInput>
                                    </Stack>
                                )}
                            />
                        </div>
                        <div className='text-center'>
                            <button
                                type='button'
                                className='text-sm font-medium text-indigo-600 hover:text-indigo-500'
                                onClick={onResendCodeHandler}
                            >
                                Resend code
                            </button>
                            {!isForgotPasswordPage && (
                                <div className='mt-2'>
                                    <button
                                        type='button'
                                        className='text-xs text-gray-500 hover:text-gray-700'
                                        onClick={onResendCodeHandler}
                                    >
                                        Need to use a different number?
                                    </button>
                                </div>
                            )}
                        </div>
                        <div>
                            <ZaraButton
                                isLoading={loading}
                                type='onSubmit'
                                isDisabled={isDisabled || loading}
                                width='100%'
                            >
                                {isForgotPasswordPage ? 'Send' : 'Verify Phone'}
                            </ZaraButton>
                        </div>
                    </form>
                    {isCodeSent && (
                        <div className='mt-4 p-3 bg-green-100 text-green-700 rounded-md flex items-center justify-center text-sm'>
                            <svg className='w-5 h-5 mr-2' fill='currentColor' viewBox='0 0 20 20'>
                                <path
                                    fillRule='evenodd'
                                    d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                                    clipRule='evenodd'
                                />
                            </svg>
                            Code successfully sent.
                        </div>
                    )}
                </div>
            </div>
            <RefreshPhone isOpen={isOpen} onClose={onClose} />
        </>
    );
};

export default Verification;
